import { useScheduleGroup } from "routes/facilities/lib/use-schedule-group"
import { Bound } from "lib/@components/binding/Bound"
import { usePayloadTask } from "routes/facilities/lib/use-payload-task"
import { SummaryDataChanged, TaskTailorChange } from "event-definitions"
import { Box, Tooltip } from "@mui/material"
import { BoundCheckBox } from "lib/@components/standard-checkbox"
import { confirm } from "lib/@dialogs/confirm"
import { useRefresh } from "lib/@hooks/useRefresh"
import { NotOnMobile, OnMobile } from "lib/@components/mobile"
import { BlockClicks } from "lib/@components/block-clicks"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { Payload } from "routes/schedule/controller/payload"
import { assessStatus } from "routes/schedule/assess-status"
import { RegimeDraftLockTooltip } from "../regime-draft-locking/regime-draft-lock-tooltip"

export function Included() {
    const name = useScheduleGroup()
    const { target, payload, schedule, isRegimeDraftLocked } = useBoundContext()
    const task = usePayloadTask()
    // Never write `included` on render. An absent key already means "included", so seeding it with
    // `true` stored a value carrying no information at all, on every task row the editor rendered,
    // and any later save persisted the lot. The checkbox reads its state from `defaultValue` below
    // instead, which resolves an absent key without touching the payload. Excluding a task writes
    // `included: false`, because that is a real decision; re-including removes the key again.
    const refresh = useRefresh()
    SummaryDataChanged.raiseOnce()
    return (
        <Payload>
            <BlockClicks>
                <Bound onChange={validate} target={task}>
                    <Tooltip title={!isRegimeDraftLocked ? `Include task for ${name}` : ""}>
                        <Box position="relative">
                            <NotOnMobile>
                                <RegimeDraftLockTooltip>
                                    <BoundCheckBox
                                        onChanged={warn}
                                        defaultValue={true}
                                        sx={{ mt: 0.3, ml: -4, mr: 0 }}
                                        // disabled={!["Amber", "Green"].includes(target.classification)}
                                        field="included"
                                        label=""
                                        data-cy="included-task-checkbox"
                                    />
                                </RegimeDraftLockTooltip>
                            </NotOnMobile>
                            <OnMobile>
                                <RegimeDraftLockTooltip>
                                    <BoundCheckBox
                                        onChanged={warn}
                                        defaultValue={true}
                                        sx={{ position: "absolute", left: -46, top: 20 }}
                                        // disabled={!["Amber", "Green"].includes(target.classification)}
                                        field="included"
                                        label=""
                                    />
                                </RegimeDraftLockTooltip>
                            </OnMobile>
                        </Box>
                    </Tooltip>
                </Bound>
            </BlockClicks>
        </Payload>
    )

    async function warn() {
        if (target.classification === "Red" && task.included === false) {
            if (
                !(await confirm(
                    "You have tried to disable a Red task.  Red tasks are required for legal compliance and should rarely be excluded, the only justification for disabling a Red task is that it relates to a non-existent part or feature for your specific use case.  Would you like to continue and disable the task?",
                    "Attention Required"
                ))
            ) {
                // Cancelling the exclusion puts the task back to inheriting, which is an absent key
                // rather than `true`.
                delete task.included
                refresh()
            }
        }
    }

    function validate() {
        // Re-including a task means "no override" again, so drop the key instead of storing `true`.
        // Only `false` carries a decision worth keeping. This also cleans up as people work: a task
        // already carrying a redundant `true` from before this fix loses it the next time anybody
        // toggles it.
        if (task.included === true) delete task.included
        assessStatus(payload, schedule)
        TaskTailorChange.raiseOnce({ payload, taskId: target.id })
    }
}
